import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tab, Nav, Card, Button, Badge, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const IITJAMMathStatPage = () => {
  const [syllabusData, setSyllabusData] = useState([]);
  const [papersData, setPapersData] = useState([]);
  const [booksData, setBooksData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulate fetching data from database
  useEffect(() => {
    // In a real application, these would be API calls
    const fetchData = () => {
      setTimeout(() => {
        setSyllabusData([
          { id: 1, title: 'Calculus', topics: ['Limits', 'Continuity', 'Differentiability', 'Mean Value Theorems', 'Taylor Series', 'Maxima and Minima', 'Multiple Integrals'] },
          { id: 2, title: 'Linear Algebra', topics: ['Vector Spaces', 'Linear Transformations', 'Matrices', 'Determinants', 'Systems of Linear Equations', 'Eigenvalues and Eigenvectors'] },
          { id: 3, title: 'Probability', topics: ['Axioms of Probability', 'Conditional Probability', 'Independence', 'Random Variables', 'Probability Distributions', 'Expectation', 'Moments', 'Moment Generating Functions'] },
          { id: 4, title: 'Statistics', topics: ['Sampling Distributions', 'Point Estimation', 'Interval Estimation', 'Hypothesis Testing', 'Regression Analysis', 'ANOVA'] },
          { id: 5, title: 'Stochastic Processes', topics: ['Markov Chains', 'Poisson Process', 'Queuing Theory', 'Time Series Analysis'] },
        ]);
        
        setPapersData([
          { id: 1, year: '2024', paperLink: '#', answerKeyLink: '#' },
          { id: 2, year: '2023', paperLink: '#', answerKeyLink: '#' },
          { id: 3, year: '2022', paperLink: '#', answerKeyLink: '#' },
          { id: 4, year: '2021', paperLink: '#', answerKeyLink: '#' },
          { id: 5, year: '2020', paperLink: '#', answerKeyLink: '#' },
          { id: 6, year: '2019', paperLink: '#', answerKeyLink: '#' },
          { id: 7, year: '2018', paperLink: '#', answerKeyLink: '#' },
        ]);
        
        setBooksData([
          { id: 1, title: 'Mathematical Statistics with Applications', author: 'Wackerly, Mendenhall & Scheaffer', coverImg: '/api/placeholder/200/300', rating: 4.8 },
          { id: 2, title: 'Introduction to Probability and Statistics', author: 'Milton & Arnold', coverImg: '/api/placeholder/200/300', rating: 4.7 },
          { id: 3, title: 'An Introduction to Probability Theory and Its Applications', author: 'William Feller', coverImg: '/api/placeholder/200/300', rating: 4.9 },
          { id: 4, title: 'Linear Algebra and Its Applications', author: 'Gilbert Strang', coverImg: '/api/placeholder/200/300', rating: 4.6 },
          { id: 5, title: 'Calculus: Early Transcendentals', author: 'James Stewart', coverImg: '/api/placeholder/200/300', rating: 4.5 },
        ]);
        
        setLoading(false);
      }, 1000);
    };
    
    fetchData();
  }, []);

  const internships = [
    {
      id: 1,
      title: "Statistical Data Analysis",
      description: "Apply statistical techniques to analyze large datasets and extract meaningful insights for business decision-making.",
      duration: "3 months",
      stipend: "₹18,000/month",
      requirements: ["Knowledge of R or Python", "Basic understanding of statistical tests", "Data visualization skills"]
    },
    {
      id: 2,
      title: "Financial Modeling with Statistics",
      description: "Develop statistical models for financial forecasting, risk assessment, and portfolio optimization.",
      duration: "6 months",
      stipend: "₹22,000/month",
      requirements: ["Time series analysis knowledge", "Experience with financial data", "Strong mathematical foundation"]
    },
    {
      id: 3,
      title: "Predictive Analytics in Healthcare",
      description: "Use statistical methods to develop predictive models for patient outcomes and healthcare resource optimization.",
      duration: "4 months",
      stipend: "₹20,000/month",
      requirements: ["Statistical modeling experience", "Knowledge of healthcare data", "Proficiency in statistical software"]
    }
  ];

  return (
    <div className="iit-jam-mathstat-page">
      {/* Coaching Information Section with Tabs */}
      <section className="coaching-section py-5 bg-light my-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <h2 className="fw-bold">IIT-JAM Mathematical Statistics Coaching</h2>
            <p className="text-muted">Comprehensive preparation for one of India's most competitive exams</p>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading course materials...</p>
            </div>
          ) : (
            <Tab.Container id="coaching-tabs" defaultActiveKey="syllabus">
              <Nav variant="pills" className="nav-tabs-custom justify-content-center mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="syllabus">Syllabus</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="papers">Previous Papers</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="books">Reference Books</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="program">Our Program</Nav.Link>
                </Nav.Item>
              </Nav>
              
              <Tab.Content>
                {/* Syllabus Tab */}
                <Tab.Pane eventKey="syllabus">
                  <div className="syllabus-container">
                    <Row>
                      {syllabusData.map(unit => (
                        <Col md={6} lg={4} key={unit.id} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Body>
                              <Card.Title className="fw-bold">{unit.title}</Card.Title>
                              <ul className="mt-3">
                                {unit.topics.map((topic, index) => (
                                  <li key={index}>{topic}</li>
                                ))}
                              </ul>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Tab.Pane>
                
                {/* Previous Papers Tab */}
                <Tab.Pane eventKey="papers">
                  <div className="papers-container">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Year</th>
                            <th>Question Paper</th>
                            <th>Answer Key</th>
                          </tr>
                        </thead>
                        <tbody>
                          {papersData.map(paper => (
                            <tr key={paper.id}>
                              <td>{paper.year}</td>
                              <td>
                                <Button variant="link" href={paper.paperLink}>
                                  Download Paper
                                </Button>
                              </td>
                              <td>
                                <Button variant="link" href={paper.answerKeyLink}>
                                  Download Answer Key
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Tab.Pane>
                
                {/* Reference Books Tab */}
                <Tab.Pane eventKey="books">
                  <div className="books-container">
                    <Row className="g-4">
                      {booksData.map(book => (
                        <Col md={6} lg={4} key={book.id}>
                          <Card className="h-100 book-card shadow-sm">
                            <Row className="g-0">
                              <Col xs={4}>
                                <img src={book.coverImg} alt={book.title} className="img-fluid rounded-start h-100 object-fit-cover" />
                              </Col>
                              <Col xs={8}>
                                <Card.Body>
                                  <Card.Title className="h5">{book.title}</Card.Title>
                                  <Card.Text className="text-muted">{book.author}</Card.Text>
                                  <div className="d-flex align-items-center mt-2">
                                    <span className="me-2">Rating:</span>
                                    <Badge bg="success">{book.rating}/5.0</Badge>
                                  </div>
                                </Card.Body>
                              </Col>
                            </Row>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Tab.Pane>
                
                {/* Our Program Tab */}
                <Tab.Pane eventKey="program">
                  <Row>
                    <Col lg={6} className="mb-4">
                      <div className="program-features">
                        <h3 className="h4 mb-4">Our Comprehensive Program Features</h3>
                        <ul className="feature-list">
                          <li>
                            <div className="d-flex align-items-center mb-3">
                              <div className="feature-icon me-3">📊</div>
                              <div>
                                <h4 className="h5 mb-1">Expert Faculty</h4>
                                <p className="mb-0">Learn from IIT alumni and statistical experts with years of teaching experience</p>
                              </div>
                            </div>
                          </li>
                          <li>
                            <div className="d-flex align-items-center mb-3">
                              <div className="feature-icon me-3">🧮</div>
                              <div>
                                <h4 className="h5 mb-1">Hands-on Problem Solving</h4>
                                <p className="mb-0">Intensive practice with real-world statistical problems and applications</p>
                              </div>
                            </div>
                          </li>
                          <li>
                            <div className="d-flex align-items-center mb-3">
                              <div className="feature-icon me-3">📝</div>
                              <div>
                                <h4 className="h5 mb-1">Regular Mock Tests</h4>
                                <p className="mb-0">Weekly tests to gauge your preparation and identify weak areas</p>
                              </div>
                            </div>
                          </li>
                          <li>
                            <div className="d-flex align-items-center mb-3">
                              <div className="feature-icon me-3">💻</div>
                              <div>
                                <h4 className="h5 mb-1">Statistical Software Training</h4>
                                <p className="mb-0">Learn R, Python, and other statistical tools used in industry</p>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="program-schedule bg-white p-4 rounded shadow-sm">
                        <h3 className="h4 mb-4">Program Schedule</h3>
                        <div className="schedule-item mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between">
                            <h4 className="h5 mb-1">Foundation Phase</h4>
                            <span className="badge bg-primary">2 Months</span>
                          </div>
                          <p className="mb-0">Calculus, Linear Algebra and Basic Probability concepts</p>
                        </div>
                        <div className="schedule-item mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between">
                            <h4 className="h5 mb-1">Advanced Phase</h4>
                            <span className="badge bg-primary">3 Months</span>
                          </div>
                          <p className="mb-0">Advanced Statistics, Stochastic Processes and Statistical Inference</p>
                        </div>
                        <div className="schedule-item mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between">
                            <h4 className="h5 mb-1">Revision Phase</h4>
                            <span className="badge bg-primary">1 Month</span>
                          </div>
                          <p className="mb-0">Comprehensive revision and doubt clearing sessions</p>
                        </div>
                        <div className="schedule-item">
                          <div className="d-flex justify-content-between">
                            <h4 className="h5 mb-1">Test Series</h4>
                            <span className="badge bg-primary">Throughout</span>
                          </div>
                          <p className="mb-0">Regular mock tests under exam conditions</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Container>
      </section>

      {/* Internship Section */}
      <section className="internship-section py-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <h2 className="fw-bold">Statistical Analysis Internship Opportunities</h2>
            <p className="text-muted">Gain practical experience alongside your IIT-JAM preparation</p>
          </div>

          <Row className="g-4">
            {internships.map(internship => (
              <Col md={6} lg={4} key={internship.id}>
                <Card className="h-100 internship-card shadow">
                  <Card.Body>
                    <Badge bg="info" className="mb-3">Data Analysis</Badge>
                    <Card.Title className="h4">{internship.title}</Card.Title>
                    <Card.Text>{internship.description}</Card.Text>
                    
                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <small className="text-muted d-block">Duration</small>
                        <strong>{internship.duration}</strong>
                      </div>
                      <div>
                        <small className="text-muted d-block">Stipend</small>
                        <strong>{internship.stipend}</strong>
                      </div>
                    </div>
                    
                    <div className="requirements mt-3">
                      <small className="text-muted d-block mb-2">Requirements:</small>
                      <ul className="ps-3 mb-0">
                        {internship.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </Card.Body>
                  <Card.Footer className="bg-white border-0">
                    <Button variant="outline-primary" className="w-100">Apply Now</Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="internship-benefits mt-5">
            <Row className="g-4 align-items-center">
              <Col lg={6}>
                <div className="benefits-content p-4 bg-light rounded">
                  <h3 className="h4 mb-4">Why Choose Our Integrated Internship Program?</h3>
                  <div className="benefit-item d-flex mb-3">
                    <div className="benefit-icon me-3">📈</div>
                    <div>
                      <h4 className="h5 mb-1">Real-world Analysis</h4>
                      <p className="mb-0">Work on actual industry data and statistical modeling projects</p>
                    </div>
                  </div>
                  <div className="benefit-item d-flex mb-3">
                    <div className="benefit-icon me-3">🧠</div>
                    <div>
                      <h4 className="h5 mb-1">Applied Statistics</h4>
                      <p className="mb-0">See how theoretical concepts from IIT-JAM syllabus apply in practice</p>
                    </div>
                  </div>
                  <div className="benefit-item d-flex mb-3">
                    <div className="benefit-icon me-3">💹</div>
                    <div>
                      <h4 className="h5 mb-1">Career Advancement</h4>
                      <p className="mb-0">Build your resume with prestigious data analysis experience</p>
                    </div>
                  </div>
                  <div className="benefit-item d-flex">
                    <div className="benefit-icon me-3">🤝</div>
                    <div>
                      <h4 className="h5 mb-1">Industry Connections</h4>
                      <p className="mb-0">Network with statisticians and potential employers</p>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className="text-center">
                  <img src="/api/placeholder/500/400" alt="Students analyzing statistical data" className="img-fluid rounded shadow" />
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default IITJAMMathStatPage;

// Add CSS for styling
export const additionalCSS = `
.nav-tabs-custom {
  border-radius: 50px;
  overflow: hidden;
  display: inline-flex;
  border: 1px solid #dee2e6;
  background-color: white;
}

.nav-tabs-custom .nav-link {
  border-radius: 0;
  border: none;
  color: #495057;
  padding: 0.75rem 1.5rem;
}

.nav-tabs-custom .nav-link.active {
  background-color: #0d6efd;
  color: white;
}

.feature-icon {
  font-size: 1.75rem;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(13, 110, 253, 0.1);
}

.benefit-icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-list {
  list-style: none;
  padding-left: 0;
}

.internship-card {
  transition: transform 0.3s ease;
}

.internship-card:hover {
  transform: translateY(-5px);
}

.book-card img {
  max-height: 200px;
  object-fit: cover;
}

@media (max-width: 768px) {
  .nav-tabs-custom {
    flex-wrap: wrap;
  }
  
  .nav-tabs-custom .nav-link {
    padding: 0.5rem 1rem;
  }
}

/* Create scrollable containers for content-heavy tabs */
.syllabus-container, .papers-container, .books-container {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 5px;
}

.syllabus-container::-webkit-scrollbar,
.papers-container::-webkit-scrollbar,
.books-container::-webkit-scrollbar {
  width: 6px;
}

.syllabus-container::-webkit-scrollbar-thumb,
.papers-container::-webkit-scrollbar-thumb,
.books-container::-webkit-scrollbar-thumb {
  background-color: #dee2e6;
  border-radius: 3px;
}

.syllabus-container::-webkit-scrollbar-track,
.papers-container::-webkit-scrollbar-track,
.books-container::-webkit-scrollbar-track {
  background-color: #f8f9fa;
}
`;